import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { withFirebase } from '../Fb'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import moment from 'moment'
import { parseTicketQuantity } from '../../eventtickets'

function CurrencyField({ value, onChange, currency }) {

  const [currencyValue, setCurrencyValue] = useState({ integer: 0, decimal: 0 })

  useEffect(() => {
    setCurrencyValue({ integer: Math.floor(value), decimal: (value / 100).toFixed(2).replace('.', ',') })
  }, [value])

  const _onChange = (e) => {
    const cleaned = e.target.value.trim().replaceAll(' ', '').replace(',', '.')
    const integer = Math.floor(parseFloat(cleaned) * 100)
    setCurrencyValue({ integer, decimal: e.target.value })
    if (onChange) {
      onChange({ target: { value: integer } })
    }
  }


  return (
    <TextField
      value={currencyValue.decimal}
      onChange={_onChange}
      InputProps={{
        endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
      }}
    />
  )
}


function ProductLine({ product, index, handleRemoveFromList, updateProduct }) {
  // Raw text while typing; the row in the list gets the parsed number, or null when it cannot be
  // saved (0, negative, decimal, empty), which keeps Save disabled.
  const [quantity, setQuantity] = useState('1')
  const [price, setPrice] = useState(product.price)
  const [name, setName] = useState(product.name)

  const handleChange = (event, property) => {
    if (property === 'price') {
      setPrice(event.target.value)
      updateProduct(index, { price: event.target.value })
    } else if (property === 'quantity') {
      setQuantity(event.target.value)
      updateProduct(index, { quantity: parseTicketQuantity(event.target.value) })
    } else if (property === 'name') {
      setName(event.target.value)
      updateProduct(index, { name: event.target.value })
    }
  }

  const parsedQuantity = parseTicketQuantity(quantity)
  const quantityInvalid = parsedQuantity === null

  return (
    <>
      <Grid item xs={2}>
        {product.productCode}
      </Grid>
      <Grid item xs={4}>
        <TextField fullWidth value={name} onChange={(e) => handleChange(e, 'name')} />
      </Grid>
      <Grid item xs={2}>
        <CurrencyField value={price} onChange={(e) => handleChange(e, 'price')} currency='€' />
      </Grid>
      <Grid item xs={1}>
        <TextField
          value={quantity}
          onChange={(e) => handleChange(e, 'quantity')}
          type='number'
          inputProps={{ min: 1, step: 1 }}
          error={quantityInvalid}
          helperText={quantityInvalid ? 'Vähintään 1' : ''}
        />
      </Grid>
      <Grid item xs={1}>
        {product.vat} %
      </Grid>
      <Grid item xs={1}>
        {((price / 100) * (parsedQuantity || 0)).toFixed(2).replace('.',',')} €
      </Grid>
      <Grid item xs={1}>
        <IconButton onClick={() => handleRemoveFromList(index)}>
          <DeleteIcon />
        </IconButton>
      </Grid>
    </>
  )
}

function EventTicketDialog({ open, onClose, title, firebase, payer, onUpdate }) {
  const [products, setProducts] = useState([])
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [productList, setProductList] = useState([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [addPayment, setAddPayment] = useState(true)

  useEffect(() => {
    if (open) {
      firebase.getEventTickets().then((results) => {
        // expand per vaiantions
        results = results.flatMap((ticket) => {
          if (ticket.variations && ticket.variations.length > 0) {
            return ticket.variations.map((variation) => ({
              ...ticket,
              ...variation,
              id: `${ticket.id}-${variation.id}`,
              name: ticket.name + ' - ' + variation.name,
              price: variation.price * 100, // assuming price is in euros
            }))
          } else {
            return [{ ...ticket, id: ticket.id, name: ticket.name, price: ticket.price * 100 }]
          }
        })
//        results.sort((a, b) => a.name.localeCompare(b.name))
          setProducts(results)
          setLoading(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleChange = (event) => {
    const product = products.find((x) => x.id === event.target.value)
    setProduct(product)
  }

  const handleAddToList = () => {
    setProductList([...productList, product])
    setProduct(null)
  }

  const handleRemoveFromList = (index) => {
    setProductList(productList.filter((x, i) => i !== index))
  }

  const handleClose = () => {
    setProductList([])
    setProduct(null)
    setSaveError(null)
    setAddPayment(true)
    if (onClose) onClose()
  }

  const updateList = (index, product) => {
    const newList = [...productList]
    newList[index] = { ...newList[index], ...product }
    console.log(newList)
    setProductList(newList)
  }

  // A row's quantity: 1 until the admin edits it, null when the edit cannot be saved.
  const rowQuantity = (product) => (product.quantity === undefined ? 1 : parseTicketQuantity(product.quantity))
  const hasInvalidQuantity = productList.some((p) => rowQuantity(p) === null)

  const handleSave = () => {
    const USERKEYS = ['id', 'firstname', 'lastname', 'email', 'phone', 'streetaddress', 'city', 'zip']
    const ORDERKEYS = ['id', 'quantity', 'price']
    const user = {}
    Object.keys(payer).forEach((key) => {
      if (USERKEYS.includes(key)) {
        if (payer[key]) {
          user[key] = payer[key]
        }
      }
    })
    const orders = productList.map((product) => {
      const order = {}
      Object.keys(product).forEach((key) => {
        if (ORDERKEYS.includes(key)) {
          order[key] = product[key]
        }
      })
      if (order.price) {
        order.price = parseInt(order.price) // ensure price is an integer
      } else {
        order.price = 0 // default price to 0 if not specified
      }
      if (order.quantity === undefined) {
        order.quantity = 1 // default quantity to 1 if not specified
      }
      if (order.id) {
        const [oid, vid] = order.id.split('-')
        order.id = oid // use the product id as the order id
        order.variationId = vid // store the variation id separately if needed
      }
      // The payment deadline shown in the dialog. Once it passes without a payment the backend
      // releases the tickets (see takesTicket in src/eventtickets.js). Written in UTC with an
      // offset: the backend reads a bare time as UTC, which moved the deadline 2-3 h later.
      order.validuntil = moment().add(1, 'day').startOf('hour').utc().format('YYYY-MM-DD HH:mm:ssZ')
      return order
    })

    if (orders.length === 0) {
      console.error('No valid orders to save')
      return
    }
    // The backend would create a ticket row with quantity 0 (or null) that nobody can use.
    if (orders.some((order) => parseTicketQuantity(order.quantity) === null)) {
      setSaveError('Jokaisen rivin määrän pitää olla vähintään 1.')
      return
    }
    if (orders.some(order => order.variationId === 'undefined')) {
      console.error('Some orders have undefined variationId')
      return
    }
    
    setSaving(true)
    setSaveError(null)
    firebase.addEventTickets(orders, user, addPayment).then((response) => {
      setSaving(false)
      // addEventTickets catches its own errors and resolves with { error }, so a failure
      // never reaches .catch below. Without this check the dialog closed as if the save
      // had worked.
      if (response && response.error) {
        console.error('Error saving tickets:', response.error)
        setSaveError(response.error)
        return
      }
      setProductList([])
      setProduct(null)
      if (onUpdate) onUpdate()
      if (onClose) onClose()
    }).catch((error) => {
      console.error('Error saving tickets:', error)
      setSaving(false)
      setSaveError(error && error.message ? error.message : String(error))
    })
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading && <DialogContentText>Ladataan...</DialogContentText>}
        <FormControl style={{ minWidth: 160 }}>
          <InputLabel id='product-select-label'>Lippu</InputLabel>
          <Select
            labelId='product-select-label'
            id='product-select'
            value={product ? product.id : ''}
            onChange={handleChange}
          >
            <MenuItem value={''} disabled>
              Valitse
            </MenuItem>
            {products.map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Labelled rather than a bare icon: the selection is only staged until it is
            added to the list, and Save stays disabled until the list is non empty. */}
        <Button
          onClick={handleAddToList}
          disabled={!product}
          startIcon={<AddCircleIcon />}
          style={{ marginLeft: 8 }}
        >
          Lisää listalle
        </Button>
      </DialogContent>
      <DialogContent>
        <Grid container spacing={1} direction='row' justifyContent='center' alignItems='center'>
          {productList.map((product, index) => {
            if (product.quantity === undefined) product.quantity = 1
            return (
              // ProductLine keeps name/price/quantity in its own state, so without a key
              // React reuses instances by position and removing a row leaves the previous
              // row's values behind on the ones after it.
              <ProductLine
                key={product.id + '-' + index}
                product={product}
                index={index}
                handleRemoveFromList={handleRemoveFromList}
                updateProduct={updateList}
              />
            )
          })}
        </Grid>
        {hasInvalidQuantity && (
          <Alert severity='warning' style={{ marginTop: 8 }}>
            Jokaisen rivin määrän pitää olla vähintään 1. Korjaa määrä tai poista rivi.
          </Alert>
        )}
        {product && (
          <Alert severity='info' style={{ marginTop: 8 }}>
            Valittu lippu ei ole vielä listalla. Lisää se painamalla "Lisää listalle".
          </Alert>
        )}
        <FormControlLabel
          style={{ marginTop: 8 }}
          control={
            <Checkbox
              checked={addPayment}
              onChange={(e) => setAddPayment(e.target.checked)}
              disabled={saving}
            />
          }
          label='Lisää myös maksu'
        />
        {saveError && (
          <Alert severity='error' style={{ marginTop: 8 }}>
            Lippujen tallennus epäonnistui: {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {/* Only relevant when the payment is left for the customer to make. */}
        {!addPayment && (
          <Alert severity='warning'>
            Huom! Liput täytyy maksaa ennen {moment().add(1, 'day').startOf('hour').format('DD.MM.YYYY H:mm')}, jotta ne ovat voimassa.
          </Alert>
        )}
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || productList.length === 0 || hasInvalidQuantity} color='primary' variant='contained'>
          Save {saving && <CircularProgress size={20} />}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withFirebase(EventTicketDialog)
